// @flow

import { Amplify } from 'aws-amplify';
import {
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  fetchAuthSession,
  getCurrentUser,
  updatePassword,
  updateUserAttributes,
  rememberDevice,
} from 'aws-amplify/auth';
import { UserNotFoundError, UserAlreadyVerifiedError } from './errors';
import { aws, userManagementRootEndpoint } from '../../config';
import { store } from '../../state/store';
import { ACTION_TYPES } from '../../state/actions';

const { userPoolId, clientId } = aws;

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId,
      userPoolClientId: clientId,
    },
  },
});

// Registration
export function resendConfirmation(userEmail: string): Promise<any> {
  return fetch(
    `${userManagementRootEndpoint}/v3/registration/resend-confirmation-email`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: userEmail,
      }),
    }
  ).then(res => {
    switch (res.status) {
      case 200:
        return;
      case 404:
        throw new UserNotFoundError(userEmail);
      case 409:
        throw new UserAlreadyVerifiedError(userEmail);
      default:
        throw new Error();
    }
  });
}

// Auth
function setCredentials(token?: string) {
  let user = null;
  if (token) {
    const base64Url = token.split('.')[1];
    user = JSON.parse(window.atob(base64Url));
  }
  store.dispatch({ type: ACTION_TYPES.SET_LOGGED_IN_USER, payload: user });
}

async function buildUserSession(): Promise<any> {
  const session = await fetchAuthSession();
  const { tokens } = session;
  if (!tokens) {
    throw new Error('Session Timed Out');
  }
  setCredentials(tokens.idToken.toString());
  return {
    cognitoObject: {
      signInUserSession: {
        accessToken: {
          jwtToken: tokens.accessToken.toString(),
          payload: tokens.accessToken.payload,
        },
      },
    },
    info: tokens.idToken.payload,
  };
}

export function signOut() {
  amplifySignOut().catch(() => {});
  store.dispatch({ type: ACTION_TYPES.SET_LOGGED_IN_USER, payload: null });
}

export function login(email: string, password: string): Promise<any> {
  return amplifySignIn({ username: email, password }).then(
    async ({ nextStep }) => {
      if (nextStep.signInStep === 'CONFIRM_DEVICE') {
        await rememberDevice();
      }
      return buildUserSession();
    }
  );
}

export function checkSessionWithPromise(): Promise<any> {
  return getCurrentUser()
    .then(() => buildUserSession())
    .catch(() => {
      throw new Error('Session Timed Out');
    });
}

export function getUserFromSession(): Promise<any> {
  return getCurrentUser()
    .then(() => buildUserSession())
    .catch(() => {
      throw false;
    });
}

export function changePassword(
  oldPassword: string,
  newPassword: string
): Promise<*> {
  return fetchAuthSession()
    .then(session => {
      if (!session.tokens) {
        throw new Error('Session Timed Out');
      }
      return updatePassword({ oldPassword, newPassword });
    })
    .then(() => fetchAuthSession({ forceRefresh: true }))
    .then(session => {
      setCredentials(session.tokens?.idToken?.toString());
    });
}

export function updateProfile(
  user: any,
  updates: Record<string, unknown>
): Promise<any> {
  return fetchAuthSession()
    .then(session => {
      if (!session.tokens) {
        throw new Error('Session Timed Out');
      }
      return updateUserAttributes({ userAttributes: updates });
    })
    .then(() => fetchAuthSession({ forceRefresh: true }))
    .then(session => {
      setCredentials(session.tokens?.idToken?.toString());
    });
}

// Param transformation
export function toRegularTelephone(telephone: string): string {
  if (!telephone) {
    return '';
  }
  return `${telephone.slice(2, 5)} ${telephone.slice(5, 8)}-${telephone.slice(
    8
  )}`;
}

export function toCognitoPhone(phone: string): string {
  phone = phone.replace(/\(|\)|-|\s|\+/g, '');
  return `+${phone.length === 11 ? '' : '1'}${phone}`;
}
